import { TimezoneEnum } from '../constants'
import type { CoreModuleOptions, DurationOption } from '../types'
import { useDayjs, useCoreConfig, useClock } from '#imports'

export interface useFormatOptions {
    toParts?: boolean
    decimals?: number
    removeTrailingZeros?: boolean
}

function asNumberFormat(value: number, lang: string, currency: string | undefined, options: useFormatOptions) {
    const formatOptions: Intl.NumberFormatOptions = {
        currency,
        style: currency ? 'currency' : 'percent',
        minimumFractionDigits: options.decimals,
        maximumFractionDigits: options.decimals,
        trailingZeroDisplay: options.removeTrailingZeros ? 'stripIfInteger' : 'auto'
    }

    if (options.toParts) {
        return new Intl.NumberFormat(lang, formatOptions).formatToParts(value)
    }

    return new Intl.NumberFormat(lang, formatOptions).format(value)
}

export interface UseFormatAsDateOptions {
    format: string
    useMonolithTimezone: boolean
}

export interface UseFormatAsRelativeTimeOptions {
    timeFormat: 'minimal' | 'short' | 'long'
    length: number
    smallestUnit: keyof CoreModuleOptions['translations']['durations']
    largestUnit: keyof CoreModuleOptions['translations']['durations']
    useRelativeTime: boolean
    useRealTime: boolean
}

export interface UseFormatAsStaticTimeOptions {
    timeFormat?: 'minimal' | 'short' | 'long'
    length?: number
    smallestUnit?: keyof CoreModuleOptions['translations']['durations']
    largestUnit?: keyof CoreModuleOptions['translations']['durations']
}

export const useFormat = () => {
    const config = useCoreConfig()
    const dayjs = useDayjs()
    const { clock } = useClock()

    const asMoney = (value: number, options: useFormatOptions = { decimals: 2, toParts: false, removeTrailingZeros: false }) => asNumberFormat(value, config.locale, config.currency, options)
    const asPercent = (value: number, options: useFormatOptions = { decimals: 1, toParts: false, removeTrailingZeros: false }) => asNumberFormat(value, config.locale, undefined, options)

    // Date & Time
    function asDateFormat(value: Parameters<ReturnType<typeof useDayjs>>[0], options: UseFormatAsDateOptions): string {
        const { format, useMonolithTimezone } = options

        if (useMonolithTimezone) {
            return dayjs.tz(value as string | undefined, TimezoneEnum.DK).format(format)
        }

        return dayjs(value).tz().format(format)
    }

    const asDate = (value: Parameters<ReturnType<typeof useDayjs>>[0], options: Partial<UseFormatAsDateOptions> = {}) => {
        const { format = config.dayjs?.defaultDateFormat || 'L', useMonolithTimezone = false } = options

        return asDateFormat(value, {
            format,
            useMonolithTimezone,
        })
    }
    const asTime = (value: Parameters<ReturnType<typeof useDayjs>>[0], options: Partial<UseFormatAsDateOptions> = {}) => {
        const { format = config.dayjs?.defaultTimeFormat || 'LTS', useMonolithTimezone = false } = options

        return asDateFormat(value, {
            format,
            useMonolithTimezone,
        })
    }
    const asDatetime = (value: Parameters<ReturnType<typeof useDayjs>>[0], options: Partial<UseFormatAsDateOptions> = {}) => {
        const { format = config.dayjs?.defaultDatetimeFormat || 'L LTS', useMonolithTimezone = false } = options

        return asDateFormat(value, {
            format,
            useMonolithTimezone,
        })
    }
    // ./ Date & Time

    /**
     * @example
     * asRelativeTime(futureTime, { timeFormat: 'long', length: 3 })
     * Result: "in 22 hours 23 minutes and 21 seconds"
     * asRelativeTime(futureTime, { timeFormat: 'long', length: 2 })
     * Result: "in 22 hours and 23 minutes"
     * asRelativeTime(futureTime, { timeFormat: 'long', length: 1, useRelativeTime: false })
     * Result: "22 hours"
     * asRelativeTime(pastTime, { timeFormat: 'minimal', length: 3, useRelativeTime: false, smallestUnit: 'minutes' })
     * Result: "22h 23m"
     * asRelativeTime(pastTime, { timeFormat: 'short', length: 3, useRelativeTime: false, smallestUnit: 'minutes' })
     * Result: "22h 23min"
     */
    const asRelativeTime = (
        value: Parameters<ReturnType<typeof useDayjs>>[0],
        options: Partial<UseFormatAsRelativeTimeOptions> = {}
    ): string => {
        const {
            timeFormat = 'short',
            length = 1,
            smallestUnit = 'minutes',
            largestUnit = 'years',
            useRelativeTime = true,
            useRealTime = true,
        }: Partial<UseFormatAsRelativeTimeOptions> = options
        const now = useRealTime ? clock.value : dayjs.tz()
        const targetDate = dayjs(value).tz()
        const diffInSeconds: number = targetDate.diff(now, 'seconds')
        const isFuture: boolean = diffInSeconds > 0
        const absDiffInSeconds: number = Math.abs(diffInSeconds)

        // Handle 'just now' case
        if (absDiffInSeconds < 1) {
            return config.translations.durations.now
        }

        const result = formatDuration(absDiffInSeconds, {
            timeFormat,
            length,
            smallestUnit,
            largestUnit,
        })

        if (useRelativeTime) {
            return isFuture
                ? `${config.translations.in} ${result}`
                : `${result} ${config.translations.ago}`
        }

        return result
    }

    const formatDuration = (value: number, options: Partial<UseFormatAsRelativeTimeOptions>) => {
        const {
            timeFormat = 'short',
            length = 1,
            smallestUnit = 'minutes',
            largestUnit = 'years',
        }: Partial<UseFormatAsRelativeTimeOptions> = options
        const SECONDS_IN_MINUTE = 60
        const MINUTES_IN_HOUR = 60
        const HOURS_IN_DAY = 24
        const DAYS_IN_WEEK = 7
        const DAYS_IN_YEAR = 365
        const AVERAGE_DAYS_IN_MONTH = 365.25 / 12
        const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * MINUTES_IN_HOUR
        const SECONDS_IN_DAY = SECONDS_IN_HOUR * HOURS_IN_DAY
        const units = [
            { name: 'years', seconds: SECONDS_IN_DAY * DAYS_IN_YEAR },
            { name: 'months', seconds: SECONDS_IN_DAY * AVERAGE_DAYS_IN_MONTH },
            { name: 'weeks', seconds: SECONDS_IN_DAY * DAYS_IN_WEEK },
            { name: 'days', seconds: SECONDS_IN_DAY },
            { name: 'hours', seconds: SECONDS_IN_HOUR },
            { name: 'minutes', seconds: SECONDS_IN_MINUTE },
            { name: 'seconds', seconds: 1 },
        ]
        const smallestUnitIndex = units.findIndex(u => u.name === smallestUnit)
        const largestUnitIndex = units.findIndex(u => u.name === largestUnit)

        // if units were not found
        if (smallestUnitIndex === -1 || largestUnitIndex === -1) {
            throw new Error('Invalid smallestUnit or largestUnit')
        }

        /**
         * Gets the appropriate translation string for a time unit.
         * @param unitName - The name of the unit (e.g., 'hours').
         * @param count - The count of the unit.
         * @returns The translated unit string.
         */
        const getTranslation = (unitName: keyof CoreModuleOptions['translations']['durations'], count: number): string => {
            const unitTranslations: DurationOption | undefined | string = config.translations.durations[unitName]

            if (!unitTranslations) return ''

            switch (timeFormat) {
                case 'minimal':
                    return (unitTranslations as DurationOption).minimal

                case 'short':
                    return (unitTranslations as DurationOption).short

                case 'long':
                    return count === 1
                        ? (unitTranslations as DurationOption).long
                        : (unitTranslations as DurationOption).longPlural

                default:
                    return (unitTranslations as DurationOption).short
            }
        }

        const parts: string[] = []

        let remaining = value
        // Loop from our largest unit down to the smallest unit (inclusive)
        for (let i = largestUnitIndex; i <= smallestUnitIndex; i++) {
            const unit = units?.[i]

            if (!unit) break

            // Stop if we've reached the desired length
            if (parts.length >= length) break

            const count: number = Math.floor(remaining / unit.seconds)

            if (count >= 1) {
                const unitText: string = getTranslation(unit.name as keyof CoreModuleOptions['translations']['durations'], count)

                // Minimal uses no space before unit, others do (adjust if needed)
                // Long and Short should have spaces, Minimal should not.
                if (timeFormat === 'minimal') {
                    parts.push(`${count}${unitText}`)
                }
                else {
                    parts.push(`${count} ${unitText}`)
                }

                remaining -= count * unit.seconds
            }
        }

        // Handle edge case where no parts were added and show "1 minute".
        if (parts.length === 0) {
            // Fallback if no parts were generated (e.g. duration too short for smallestUnit)
            // Display "1 smallestUnit" or "1 minute" if smallestUnit logic led to nothing.
            const fallbackUnitToUse = smallestUnit || 'minutes'
            const count = 1
            const fallbackText: string = getTranslation(fallbackUnitToUse, count)
            const fallbackPart: string = (timeFormat === 'minimal')
                ? `${count}${fallbackText}`
                : `${count} ${fallbackText}`

            parts.push(fallbackPart)
        }

        let result: string = ''

        if (timeFormat === 'minimal') {
            result = parts.join(' ')
        }
        else {
            if (parts.length === 1) {
                result = parts[0] as string
            }
            else {
                const lastPart: string | undefined = parts.pop()

                result = `${parts.join(' ')} ${config.translations.and} ${lastPart}`
            }
        }

        return result
    }

    /**
     * @example
     * asStaticTime(80401, { timeFormat: 'long', length: 3 })
     * Result: "22 hours 23 minutes and 21 seconds"
     * asStaticTime(80401, { timeFormat: 'long', length: 2 })
     * Result: "22 hours and 23 minutes"
     * asStaticTime(80401, { timeFormat: 'long', length: 1 })
     * Result: "22 hours"
     * asStaticTime(80401, { timeFormat: 'minimal', length: 3, smallestUnit: 'minutes' })
     * Result: "22h 23m"
     * asStaticTime(80401, { timeFormat: 'short', length: 3, smallestUnit: 'minutes' })
     * Result: "22h 23min"
     * asStaticTime(3600, { timeFormat: 'short', length: 1 })
     * Result: "1 hour"
     * asStaticTime(90, { timeFormat: 'minimal', length: 2, smallestUnit: 'seconds' })
     * Result: "1m 30s"
     */
    const asStaticTime = (value: number, options: Partial<UseFormatAsStaticTimeOptions> = {}): string => {
        const { timeFormat = 'short', length = 1, smallestUnit = 'minutes', largestUnit = 'years' } = options

        return formatDuration(value, {
            timeFormat,
            length,
            smallestUnit,
            largestUnit,
        })
    }

    return {
        asMoney,
        asPercent,
        asDate,
        asTime,
        asDatetime,
        asRelativeTime,
        asStaticTime,
    }
}
